import React, { useState, useMemo } from 'react';
import { Line, Pie } from 'react-chartjs-2'; // For visualization
import { FaUserTimes, FaUsers, FaChartLine, FaHourglassHalf } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Turnover Metric Card
const TurnoverCard = ({ title, value, icon: Icon, colorClass, label }) => (
  <div className={`p-5 rounded-xl shadow-md transition duration-300 hover:shadow-lg ${colorClass}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <Icon className="text-2xl text-gray-500" />
    </div>
    <div className="mt-2 text-3xl font-bold text-gray-900">
      {value}
    </div>
    <div className="text-xs mt-1 text-gray-600">{label}</div>
  </div>
);

// 2. Turnover Data
const mockTurnoverData = {
  overall: {
    voluntary: '8.5%', // Employees who choose to leave
    involuntary: '2.0%', // Employees who were asked to leave
    total: '10.5%',
    retention: '89.5%',
    totalExits: 52,
  }
};

// --- Chart Data Setup ---

// Data for the Turnover Trend (Line Chart)
const turnoverTrendData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
  datasets: [
    {
      label: 'Total Turnover Rate (%)',
      data: [1.2, 0.8, 1.5, 1.0, 0.9, 1.8, 1.3, 1.1, 1.5],
      fill: true,
      backgroundColor: 'rgba(239, 68, 68, 0.2)', // Light Red
      borderColor: '#EF4444', // Red
      tension: 0.2,
      pointRadius: 5,
    },
  ],
};

// Data for Turnover by Exit Reason (Pie Chart)
const exitReasonData = {
  labels: ['Better Opportunity', 'Relocation', 'Company Culture', 'Performance', 'Retirement', 'Other'],
  datasets: [
    {
      data: [35, 15, 20, 10, 5, 15], // Percentage breakdown
      backgroundColor: ['#4299E1', '#F6AD55', '#D53F8C', '#48BB78', '#A0AEC0', '#ED8936'],
      hoverBackgroundColor: ['#3182CE', '#ED8936', '#B83280', '#38A169', '#718096', '#D69E2E'],
      borderWidth: 0,
    },
  ],
};

// --- Main Dashboard Component ---

const WorkforceTurnover = () => {
  const data = mockTurnoverData.overall;

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Workforce Turnover Dashboard 📉</h1>
      
      {/* 1. Key Metrics Section - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <TurnoverCard
          title="Annual Retention Rate"
          value={data.retention}
          icon={FaUsers}
          colorClass="bg-white border-l-4 border-green-500"
          label="Goal: 90%"
        />
        <TurnoverCard
          title="Total Turnover Rate"
          value={data.total}
          icon={FaUserTimes}
          colorClass="bg-white border-l-4 border-red-500"
          label={`${data.totalExits} total exits this year`}
        />
        <TurnoverCard
          title="Voluntary Turnover"
          value={data.voluntary}
          icon={FaChartLine}
          colorClass="bg-white border-l-4 border-yellow-500"
          label="Highest risk area for HR"
        />
        <TurnoverCard
          title="Avg. Employee Tenure"
          value="4.5 Years"
          icon={FaHourglassHalf}
          colorClass="bg-white border-l-4 border-blue-500"
          label="Critical metric for stability"
        />
      </div>

      {/* 2. Charts Section - Trends and Reasons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Turnover Trend Chart (Line) */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Monthly Turnover Rate Trend (%)</h2>
          <div className="h-80">
            <Line data={turnoverTrendData} options={{ maintainAspectRatio: false, scales: { y: { beginAtZero: true, max: 2.5 } } }} />
          </div>
        </div>
        
        {/* Turnover by Reason Chart (Pie) */}
        <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Turnover by Exit Reason (YTD)</h2>
          <div className="h-64 w-64">
            <Pie data={exitReasonData} options={{ maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {/* 3. Deep Dive Tables - Critical Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Turnover by Department */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Turnover by Department</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Turnover Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hired/Lost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[{ dep: 'Technology', rate: '18.2%', hired: 10, lost: 8, color: 'text-red-600' },
                  { dep: 'Finance', rate: '6.5%', hired: 5, lost: 1, color: 'text-green-600' },
                  { dep: 'Sales', rate: '12.0%', hired: 8, lost: 4, color: 'text-yellow-600' },
                ].map((item, index) => (
                  <tr key={index} className="hover:bg-blue-50 transition duration-150">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.dep}</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-bold ${item.color}`}>{item.rate}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{`${item.hired} / ${item.lost}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Turnover by Tenure (New Hires are high risk) */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Turnover by Employee Tenure</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenure Group</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate (%)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[{ group: '0-1 Year', exits: 30, rate: '25.0%', color: 'text-red-600' },
                  { group: '1-3 Years', exits: 15, rate: '10.0%', color: 'text-yellow-600' },
                  { group: '3+ Years', exits: 7, rate: '4.0%', color: 'text-green-600' },
                ].map((item, index) => (
                  <tr key={index} className="hover:bg-blue-50 transition duration-150">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.group}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.exits}</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm font-bold ${item.color}`}>{item.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkforceTurnover;